import { useEffect, useState, useRef, useCallback } from "react";
import { CertificationAssignmentList } from "./CertificationAssignmentList";
import { CertificationList } from "./CertificationList";
import { DeleteCertificationConfirmation } from "./components/DeleteCertificationConfirmation";
import { CertificationFormModal } from "../components/CertificationFormModal";
import { api } from "../../../services/api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCertifications } from "./hooks/use-certifications";
import { ExportCertification } from "./components/ExportCertification";
import { ShowToast } from "../../../services/toast";
import { useApp } from "../../../services/app-context";
import { getLabel } from "../utils/labels";
import { NotificationButton } from "../../../components/NotificationButton";
import { CertificationTypeSelect } from "../components/CertificationTypeSelect";
import { useUser } from "../../../services/user-context";
import { isAdmin, isManager } from "../../../services/utils";
import { Button } from "../../../components/Button";
import { ReactComponent as PlusIcon } from "../../../assets/icons/plus.svg";
import { UserAssignmentModal } from "../components/UserAssignmentModal";
import { DuplicateCertificationModal } from "../components/DuplicateCertificationModal";

export function AdminCertificationDashboard({ setCertificationType, certificationType }: { setCertificationType: (type: string) => void, certificationType: string }) {
  const [selectedCertification, setSelectedCertification] = useState<any>(null);
  const [isOpenDeleteCertificationConfirmation, setIsOpenDeleteCertificationConfirmation] = useState(false);
  const [isOpenExportCertificationModal, setIsOpenExportCertificationModal] = useState(false);
  const [isNewCertificationOpen, setIsNewCertificationOpen] = useState(false);
  const [isEditCertificationOpen, setIsEditCertificationOpen] = useState(false);
  const [isAddAssigneesOpen, setIsAddAssigneesOpen] = useState(false);
  const [isDuplicateCertificationOpen, setIsDuplicateCertificationOpen] = useState(false);
  const prevIsActiveRef = useRef<string | undefined>(undefined);
  const queryClient = useQueryClient();
  const { certifications, certificationsMeta, isLoadingCertifications, isFetchingCertifications, filter, setFilter, refetchCertifications, fetchNextPage, hasNextPage, isFetchingNextPage } = useCertifications();
  const { certificationAssets } = useApp();
  const labels = certificationAssets?.labels || {};
  const certificationSingular = getLabel("certification.singular", labels);
  const { userInfo } = useUser() as any;
  const role = userInfo?.role;

  const { mutate: deleteCertification, isLoading: isDeletingCertification } = useMutation({
    mutationFn: (certificationId: string) => api.deleteCertification(certificationId),
    onSuccess: () => {
      ShowToast({
        message: `${certificationSingular} deleted successfully`,
        type: "success",
      })
      setIsOpenDeleteCertificationConfirmation(false);
      setSelectedCertification(null);
      refetchCertifications();
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || `Failed to delete ${certificationSingular.toLowerCase()}`;
      ShowToast({
        message: errorMessage,
        type: "error",
      })
    },
  });


  useEffect(() => {
    const isActive = filter?.isActive;
    if (prevIsActiveRef.current !== undefined && prevIsActiveRef.current !== isActive) {
      setSelectedCertification(null);
    }
    prevIsActiveRef.current = isActive;
  }, [filter?.isActive]);

  useEffect(() => {
    if (!selectedCertification) return;
    const fromList = certifications?.find((c: any) => c.id === selectedCertification.id);
    if (!fromList) {
      setSelectedCertification(null);
      return;
    }
    if (fromList !== selectedCertification) {
      setSelectedCertification(fromList);
    }
  }, [certifications, selectedCertification]);

  useEffect(() => {
    if (selectedCertification) return;
    if (isLoadingCertifications || isFetchingCertifications) return;
    if (!certifications?.length) return;
    setSelectedCertification(certifications[0]);
  }, [
    selectedCertification,
    certifications,
    isLoadingCertifications,
    isFetchingCertifications,
  ]);

  const handleSelectCertification = useCallback((certification: any) => {
    setSelectedCertification(certification);
  }, []);

  const handleDeleteCertification = () => {
    deleteCertification(selectedCertification?.id);
  }

  const handleCreateCertification = () => {
    setIsNewCertificationOpen(true);
  }

  return (
    <div className="bg-background p-4 pt-20 lg:p-8">
      <div className="md:mb-8 flex items-center justify-between">
        <div>
          <div className="text-text-primary text-2xl font-bold">
            {getLabel("certificationProductName", labels)}
          </div>
          <div className="text-text-primary text-lg mt-3">
            {getLabel("certificationManagerAdminScreenSubtext", labels)}
          </div>
        </div>
        <div className="flex items-center gap-2">

          {isAdmin(role) && <Button variant="primary" className="!py-2.5 !rounded-full capitalize" onClick={handleCreateCertification}>
            <PlusIcon className="w-4 h-4 [&_path]:stroke-white" />
            New
          </Button>}
          {isManager(role) && <CertificationTypeSelect labels={labels} setCertificationType={setCertificationType} certificationType={certificationType} />}
          <NotificationButton />
        </div>
      </div>
      <div className="flex gap-2.5">
        <div className="flex-[2.5]">
          <CertificationList filter={filter} setFilter={setFilter} selectedCertification={selectedCertification} certifications={certifications} certificationsMeta={certificationsMeta} isLoading={isLoadingCertifications} onCreateCertification={handleCreateCertification} onSelectCertification={handleSelectCertification} onDeleteCertification={() => setIsOpenDeleteCertificationConfirmation(true)} fetchNextPage={fetchNextPage} hasNextPage={hasNextPage} isFetchingNextPage={isFetchingNextPage} />
        </div>
        <div className="flex-[4]">
          {selectedCertification && (
            <CertificationAssignmentList onAddAssignees={() => setIsAddAssigneesOpen(true)} onEditCertification={() => setIsEditCertificationOpen(true)} onDuplicateCertification={() => setIsDuplicateCertificationOpen(true)} isCertificationLoading={isLoadingCertifications} selectedCertification={selectedCertification} onExportCertification={() => setIsOpenExportCertificationModal(true)} onDeleteCertification={() => setIsOpenDeleteCertificationConfirmation(true)} />
          )}
        </div>
      </div>
      <DeleteCertificationConfirmation isOpen={isOpenDeleteCertificationConfirmation} onClose={() => setIsOpenDeleteCertificationConfirmation(false)} onConfirm={handleDeleteCertification} loading={isDeletingCertification} region={selectedCertification} />
      {selectedCertification && <ExportCertification certification={selectedCertification} isOpen={isOpenExportCertificationModal} onClose={() => setIsOpenExportCertificationModal(false)} />}
      {isNewCertificationOpen && (
        <CertificationFormModal
          isOpen={isNewCertificationOpen}
          onClose={() => {
            setIsNewCertificationOpen(false);
          }}
          onSuccess={() => {
            setIsNewCertificationOpen(false);
            refetchCertifications();
          }}
        />
      )}
      {isEditCertificationOpen && selectedCertification && (
        <CertificationFormModal
          isOpen={isEditCertificationOpen}
          certification={selectedCertification}
          onClose={() => {
            setIsEditCertificationOpen(false);
          }}
          onSuccess={async () => {
            const certId = selectedCertification?.id;
            if (certId) {
              await queryClient.invalidateQueries({ queryKey: ["certification-assignments", certId] });
            }
            const result = await refetchCertifications();
            if (!certId) return;
            const all =
              result.data?.pages?.flatMap((page: any) => page?.data?.data ?? []) ?? [];
            const fresh = all.find((c: any) => c.id === certId);
            if (fresh) setSelectedCertification(fresh);
          }}
        />
      )}
      {isAddAssigneesOpen && (
        <UserAssignmentModal
          isOpen={isAddAssigneesOpen}
          onClose={() => setIsAddAssigneesOpen(false)}
          onSuccess={async () => {
            const certId = selectedCertification?.id;
            setIsAddAssigneesOpen(false);
            if (certId) {
              await queryClient.invalidateQueries({ queryKey: ["certification-assignments", certId] });
            }
            const result = await refetchCertifications();
            if (!certId) return;
            const all =
              result.data?.pages?.flatMap((page: any) => page?.data?.data ?? []) ?? [];
            const fresh = all.find((c: any) => c.id === certId);
            if (fresh) setSelectedCertification(fresh);
          }}
          certification={selectedCertification}
        />
      )}
      {isDuplicateCertificationOpen && selectedCertification && (
        <DuplicateCertificationModal
          isOpen={isDuplicateCertificationOpen}
          sourceCertificationId={selectedCertification.id}
          onClose={() => setIsDuplicateCertificationOpen(false)}
          onSuccess={() => {
            setIsDuplicateCertificationOpen(false);
            refetchCertifications();
          }}
        />
      )}
    </div>
  );
}